import { useState, useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';

export interface Message {
  id: number;
  content: string;
  isAssistant: boolean;
  timestamp: Date;
}

export interface ChatContainerProps {
  messages: Message[];
  onSendMessage: (message: string) => void;
  isProcessing?: boolean;
  emptyStateMessage?: string;
  className?: string;
}

function ChatContainer({
  messages,
  onSendMessage,
  isProcessing = false,
  emptyStateMessage = 'Inicie uma conversa',
  className,
}: ChatContainerProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <div className={cn('h-full flex flex-col', className)}>
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
        <div className="p-4 space-y-4 min-h-full">
          {messages.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-gray-500 dark:text-gray-400">
                {emptyStateMessage}
              </div>
            </div>
          ) : (
            <>
              {messages.map(message => (
                <ChatMessage
                  key={message.id}
                  id={message.id}
                  content={message.content}
                  isAssistant={message.isAssistant}
                  timestamp={message.timestamp}
                  isLoading={isProcessing && message.content === '...' && message.isAssistant}
                />
              ))}
              {/* Invisible element to scroll to */}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        <ChatInput onSendMessage={onSendMessage} isDisabled={isProcessing} />
      </div>
    </div>
  );
}

export default ChatContainer;
